import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPhoneDisplayLines } from '../utils/phoneCanonical';
import { getCategoryById } from '../utils/reportCategories';
import { getMyReportForPhone } from '../services/firestoreService';
import ReportModal from './ReportModal';
import ClaimPhoneForm from './ClaimPhoneForm';
import PhoneNumberDisplay from './PhoneNumberDisplay';

function reportBorderClass(report) {
  const tier =
    report.alert_tier ||
    getCategoryById(report.category_id).alertTier ||
    'none';
  if (tier === 'danger') return 'border-l-4 border-red-500';
  if (tier === 'caution') return 'border-l-4 border-yellow-500';
  return 'border-l-4 border-gray-300';
}

function PhoneNumberDetails({ phoneNumber, onReportSubmitted, onClaimed }) {
  const { user } = useAuth();
  const [reportOpen, setReportOpen] = useState(false);
  const [hasExistingReport, setHasExistingReport] = useState(false);
  const [claimMessage, setClaimMessage] = useState('');

  useEffect(() => {
    if (!user || !phoneNumber?.canonicalId) {
      setHasExistingReport(false);
      return;
    }
    let cancelled = false;
    getMyReportForPhone(phoneNumber.canonicalId, user.uid).then((existing) => {
      if (!cancelled) setHasExistingReport(Boolean(existing));
    });
    return () => {
      cancelled = true;
    };
  }, [user, phoneNumber?.canonicalId]);

  const getTrustScoreColor = (score) => {
    if (score >= 0.7) return 'bg-green-100 text-green-800';
    if (score >= 0.4) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getScamRiskColor = (score) => {
    if (score >= 0.5) return 'bg-red-100 text-red-800';
    if (score >= 0.25) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-700';
  };

  const getNuisanceRiskColor = (score) => {
    if (score >= 0.5) return 'bg-yellow-100 text-yellow-800';
    if (score >= 0.25) return 'bg-amber-50 text-amber-800';
    return 'bg-gray-100 text-gray-700';
  };

  if (!phoneNumber) return null;

  const displayLines = getPhoneDisplayLines(phoneNumber);
  const isPrivateListing = Boolean(phoneNumber.isPrivate);
  const isPrivatePublic =
    isPrivateListing && !phoneNumber.canSeeReportDetails;
  const trustScore = phoneNumber.trust_score ?? 0.5;
  const scamScore = phoneNumber.scam_score ?? 0;
  const nuisanceScore =
    phoneNumber.nuisance_score ?? phoneNumber.spam_score ?? 0;
  const hasScores = (phoneNumber.total_reports ?? 0) > 0;
  const displayCompanyName =
    phoneNumber.displayCompanyName || phoneNumber.display_company_name || '';

  const hasDangerReports = (phoneNumber.reports || []).some((r) => {
    const tier =
      r.alert_tier || getCategoryById(r.category_id).alertTier;
    return tier === 'danger';
  });
  const hasCautionReports = (phoneNumber.reports || []).some((r) => {
    const tier =
      r.alert_tier || getCategoryById(r.category_id).alertTier;
    return tier === 'caution';
  });

  const showScamBanner = scamScore >= 0.35 || hasDangerReports;
  const showNuisanceBanner =
    !showScamBanner && (nuisanceScore >= 0.35 || hasCautionReports);

  const canonicalId = phoneNumber.canonicalId;
  const isOwner = user && phoneNumber.ownerId === user.uid;
  const isVerifiedOwner = isOwner && phoneNumber.verified;
  const isClaimedByOther =
    phoneNumber.ownerId && user && phoneNumber.ownerId !== user.uid && phoneNumber.verified;

  return (
    <div className="mt-6 p-6 bg-white rounded-lg shadow">
      <div className="flex flex-wrap items-start gap-3 mb-4">
        <PhoneNumberDisplay phone={phoneNumber} />
        {isPrivateListing && (
          <span className="px-2 py-1 text-xs font-semibold uppercase tracking-wide bg-gray-200 text-gray-700 rounded">
            Private
          </span>
        )}
      </div>

      {displayCompanyName && (
        <p className="mb-4 text-sm text-gray-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
          <span className="font-medium text-slate-800">Often reported as: </span>
          {displayCompanyName}
        </p>
      )}

      {phoneNumber.isEmpty && !isPrivatePublic && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
          <p className="text-blue-900 font-medium">
            {phoneNumber.isNewListing
              ? 'This number is not in our database yet'
              : 'No community reports yet'}
          </p>
          <p className="text-sm text-blue-800 mt-1">
            {phoneNumber.isNewListing
              ? 'We created a listing so you can report calls from this number. Help others by sharing what you know.'
              : 'Be the first to share your experience with this number.'}
          </p>
        </div>
      )}

      {phoneNumber.isEmpty && !isPrivatePublic && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
          <h4 className="font-semibold mb-2">About this listing</h4>
          <ul className="list-disc list-inside space-y-1">
            <li>International: {displayLines.international}</li>
            {displayLines.local && <li>Local: {displayLines.local}</li>}
            {phoneNumber.country_name && (
              <li>Country/region: {phoneNumber.country_name}</li>
            )}
            <li>Status: {phoneNumber.type || 'unknown'} · not verified</li>
            <li>Community reports: none yet</li>
          </ul>
        </div>
      )}

      {showScamBanner && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
          <p className="text-red-900 font-semibold">
            Scam or predatory behavior reported for this number
          </p>
          <p className="text-sm text-red-800 mt-1">
            Community reports flag serious risk. Do not share personal or financial details.
          </p>
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <Link to="/learn/red-flags" className="font-medium text-red-900 hover:underline">
              Scam red flags
            </Link>
            <Link to="/learn/what-not-to-do" className="font-medium text-red-900 hover:underline">
              What not to do
            </Link>
          </div>
        </div>
      )}

      {showNuisanceBanner && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
          <p className="text-yellow-900 font-medium">
            Unwanted or sales calls reported
          </p>
          <p className="text-sm text-yellow-800 mt-1">
            This number has nuisance reports (spam, sales, or robocalls). Exercise caution before
            engaging.
          </p>
        </div>
      )}

      {isPrivatePublic && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-800 font-medium">Private individual listing</p>
          <p className="text-sm text-gray-600 mt-1">
            This number is listed as belonging to a private individual, not a business.
            Risk scores reflect community reports, but individual report comments
            are not shown publicly.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div>
          <p className="text-gray-600">Country</p>
          <p className="font-semibold">{phoneNumber.country_name || 'Unknown'}</p>
        </div>
        <div>
          <p className="text-gray-600">Type</p>
          <p className="font-semibold capitalize">
            {isPrivateListing ? 'personal' : phoneNumber.type || 'unknown'}
          </p>
        </div>
        <div>
          <p className="text-gray-600">Verified owner</p>
          <p className="font-semibold">{phoneNumber.verified ? 'Yes' : 'No'}</p>
        </div>
      </div>

      {hasScores && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded ${getTrustScoreColor(trustScore)}`}>
              <p className="text-sm">Trust Score</p>
              <p className="text-2xl font-bold">{Math.round(trustScore * 100)}%</p>
            </div>
            <div className={`p-4 rounded ${getScamRiskColor(scamScore)}`}>
              <p className="text-sm">Scam Risk</p>
              <p className="text-2xl font-bold">{Math.round(scamScore * 100)}%</p>
            </div>
            <div className={`p-4 rounded ${getNuisanceRiskColor(nuisanceScore)}`}>
              <p className="text-sm">Unwanted Call Risk</p>
              <p className="text-2xl font-bold">{Math.round(nuisanceScore * 100)}%</p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">Report Summary</h4>
            <div className="bg-gray-50 p-4 rounded">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600">Total Reports</p>
                  <p className="font-semibold">{phoneNumber.total_reports ?? 0}</p>
                </div>
                <div>
                  <p className="text-gray-600">Last Reported</p>
                  <p className="font-semibold">
                    {phoneNumber.last_reported_at
                      ? new Date(
                          phoneNumber.last_reported_at?.toDate?.() ||
                            phoneNumber.last_reported_at
                        ).toLocaleDateString()
                      : 'Never'}
                  </p>
                </div>
              </div>
              {isPrivatePublic && (
                <p className="text-xs text-gray-500 mt-3">
                  Report details are hidden on private listings.
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {phoneNumber.canSeeReportDetails &&
        phoneNumber.reports &&
        phoneNumber.reports.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold mb-2">Community Reports</h4>
            <div className="space-y-2">
              {phoneNumber.reports.map((report) => (
                <div
                  key={report.id}
                  className={`p-4 bg-gray-50 rounded ${reportBorderClass(report)}`}
                >
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-semibold">{report.category_name}</span>
                    <span className="text-sm text-gray-500">
                      {report.created_at
                        ? new Date(report.created_at).toLocaleDateString()
                        : ''}
                    </span>
                  </div>
                  {report.company_name && (
                    <p className="text-xs text-slate-600 mb-1">
                      Organization: {report.company_name}
                    </p>
                  )}
                  <p className="text-sm text-gray-600">{report.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      {user && !isVerifiedOwner && !isClaimedByOther && canonicalId && (
        <div className="mt-6 p-4 border border-blue-100 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Own this number?</h4>
          {isOwner ? (
            <p className="text-sm text-blue-800 mb-3">
              You have claimed this number. Open your{' '}
              <Link to="/dashboard" className="underline font-medium">
                dashboard
              </Link>{' '}
              to send a verification code and confirm ownership.
            </p>
          ) : (
            <>
              <p className="text-sm text-blue-800 mb-3">
                Claim it to manage privacy settings and see all reports on your number.
              </p>
              {claimMessage && (
                <p className="text-sm text-green-800 bg-green-50 border border-green-100 rounded px-3 py-2 mb-3">
                  {claimMessage}
                </p>
              )}
              <ClaimPhoneForm
                compact
                presetCanonicalId={canonicalId}
                onClaimed={(result) => {
                  const msg = result?.alreadyClaimed
                    ? 'Already on your dashboard — verify ownership there.'
                    : 'Number claimed. Go to your dashboard to verify ownership.';
                  setClaimMessage(msg);
                  onClaimed?.(result);
                }}
              />
            </>
          )}
        </div>
      )}

      {isClaimedByOther && (
        <p className="mt-6 text-sm text-gray-600 bg-gray-50 border rounded px-4 py-3">
          This number has a verified owner. Contact support if you believe this is incorrect.
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        {user ? (
          <button
            className="flex-1 py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700"
            onClick={() => setReportOpen(true)}
          >
            {hasExistingReport ? 'Edit your report' : 'Report this number'}
          </button>
        ) : (
          <Link
            to="/login"
            className="flex-1 py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700 text-center"
          >
            Sign in to report this number
          </Link>
        )}
      </div>

      <ReportModal
        phoneNumber={phoneNumber}
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        onSuccess={() => {
          setHasExistingReport(true);
          onReportSubmitted?.();
        }}
      />
    </div>
  );
}

export default PhoneNumberDetails;
